import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/lib/auth-context";
import { ThemeProvider } from "@/lib/theme-context";
import { RealtimeProvider } from "@/lib/realtime";
import { TrustedDeviceApprovalProvider } from "@/lib/TrustedDeviceApprovalProvider";
import { IdentityChoiceDialog } from "@/lib/IdentityChoiceDialog";
import { WatchTogetherProvider } from "@/hooks/useWatchTogether";
import { useLanguageAutoDetect } from "@/hooks/useLanguageAutoDetect";
import ProtectedRoute from "@/components/ProtectedRoute";
import OpenInAppBanner from "@/components/OpenInAppBanner";
import ReelDeepLinkRedirect from "@/pages/ReelDeepLinkRedirect";
import PublicOnlyRoute from "@/components/PublicOnlyRoute";
import AppLayout from "@/components/layout/AppLayout";
import StorageFullNotice from "@/features/plus/StorageFullNotice";

// Lazy-loaded per route rather than imported eagerly - previously every
// page (Reels, the wallet dialogs, skAI, the admin dashboard, everything)
// was bundled into one ~1.7MB chunk downloaded before a person could even
// see the login form. Splitting per route means someone opening /auth only
// downloads /auth; the rest loads on demand, the moment (and only the
// moment) they navigate somewhere that needs it.
const AuthPage = lazy(() => import("@/pages/AuthPage"));
const QrLoginPage = lazy(() => import("@/pages/QrLoginPage"));
const ChatListPage = lazy(() => import("@/pages/ChatListPage"));
const SkaiListPage = lazy(() => import("@/pages/SkaiListPage"));
const SkaiChatPage = lazy(() => import("@/pages/SkaiChatPage"));
const WeatherPage = lazy(() => import("@/pages/WeatherPage"));
const ChatPage = lazy(() => import("@/pages/ChatPage"));
const ArchivedChatsPage = lazy(() => import("@/pages/ArchivedChatsPage"));
const MessageRequestsPage = lazy(() => import("@/pages/MessageRequestsPage"));
const FeedPage = lazy(() => import("@/pages/FeedPage"));
const SnapPage = lazy(() => import("@/pages/SnapPage"));
const ReelsPage = lazy(() => import("@/pages/ReelsPage"));
const SinglePostPage = lazy(() => import("@/pages/SinglePostPage"));
const NotificationsPage = lazy(() => import("@/pages/NotificationsPage"));
const ProfilePage = lazy(() => import("@/pages/ProfilePage"));
const FriendsPage = lazy(() => import("@/pages/FriendsPage"));
const HashtagPage = lazy(() => import("@/pages/HashtagPage"));
const UsernameRedirectPage = lazy(() => import("@/pages/UsernameRedirectPage"));
const JoinGroupPage = lazy(() => import("@/pages/JoinGroupPage"));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
const VerifyEmailPage = lazy(() => import("@/pages/VerifyEmailPage"));
const PublicChannelsPage = lazy(() => import("@/pages/PublicChannelsPage"));
const HelpPage = lazy(() => import("@/pages/HelpPage"));
const ServicesPage = lazy(() => import("@/pages/ServicesPage"));
const ServiceChannelPage = lazy(() => import("@/pages/ServiceChannelPage"));
const PlusPage = lazy(() => import("@/pages/PlusPage"));
const AdminDashboardPage = lazy(() => import("@/pages/AdminDashboardPage"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const MarketContinuePage = lazy(() => import("@/pages/MarketContinuePage"));
const PrivacyPolicyPage = lazy(() => import("@/pages/legal/PrivacyPolicyPage"));
const TermsPage = lazy(() => import("@/pages/legal/TermsPage"));
const CookiePolicyPage = lazy(() => import("@/pages/legal/CookiePolicyPage"));
const ChildSafetyPage = lazy(() => import("@/pages/legal/ChildSafetyPage"));
const DeleteAccountPage = lazy(() => import("@/pages/legal/DeleteAccountPage"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 20_000 }
  }
});

/** Shown only for the brief moment a lazy route chunk is downloading -
 * typically imperceptible on a fast connection, a beat longer on a slow
 * one, but never a blank white screen either way. */
function RouteFallback() {
  return (
    <div className="flex h-screen items-center justify-center bg-background">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  );
}

export default function App() {
  useLanguageAutoDetect();
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <RealtimeProvider>
            <TrustedDeviceApprovalProvider />
            <IdentityChoiceDialog />
            <WatchTogetherProvider>
            <Toaster />
            <BrowserRouter>
              <StorageFullNotice />
              <Suspense fallback={<RouteFallback />}>
              <Routes>
                {/* Signed-in users get bounced away from these (see
                    PublicOnlyRoute's own comment) - a login/signup screen and
                    its QR-login companion are the two places that never make
                    sense to show someone who's already authenticated. */}
                <Route element={<PublicOnlyRoute />}>
                  <Route path="/auth" element={<AuthPage />} />
                  <Route path="/auth/qr" element={<QrLoginPage />} />
                </Route>
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
                <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
                {/* Public directory - intentionally OUTSIDE ProtectedRoute so anyone can
                    browse/search without being logged in; joining still requires auth,
                    enforced by the join button/API itself, not by this route. */}
                <Route path="/channels" element={<PublicChannelsPage />} />
                <Route path="/privacy" element={<PrivacyPolicyPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/cookies" element={<CookiePolicyPage />} />
                <Route path="/child-safety" element={<ChildSafetyPage />} />
                <Route path="/delete-account" element={<DeleteAccountPage />} />
                {/* SkSnap Market sign-in: outside ProtectedRoute on purpose - it handles the signed-out state itself. */}
                <Route path="/market/continue" element={<MarketContinuePage />} />

                <Route element={<ProtectedRoute />}>
                  <Route element={<AppLayout />}>
                    <Route index element={<Navigate to="/chat" replace />} />
                    <Route path="/chat" element={<ChatListPage />} />
                    <Route path="/chat/archived" element={<ArchivedChatsPage />} />
                    <Route path="/chat/requests" element={<MessageRequestsPage />} />
                    <Route path="/chat/:conversationId" element={<ChatPage />} />
                    <Route path="/feed" element={<FeedPage />} />
                    <Route path="/snap" element={<SnapPage />} />
                    <Route path="/reels" element={<ReelsPage />} />
                    <Route path="/reel/:reelId" element={<ReelDeepLinkRedirect />} />
                    <Route path="/skai" element={<SkaiListPage />} />
                    <Route path="/skai/:conversationId" element={<SkaiChatPage />} />
                    <Route path="/weather" element={<WeatherPage />} />
                    <Route path="/post/:postId" element={<SinglePostPage />} />
                    <Route path="/notifications" element={<NotificationsPage />} />
                    <Route path="/profile" element={<ProfilePage />} />
                    <Route path="/admin" element={<AdminDashboardPage />} />
                    <Route path="/profile/:userId" element={<ProfilePage />} />
                    <Route path="/friends" element={<FriendsPage />} />
                    <Route path="/hashtag/:tag" element={<HashtagPage />} />
                    <Route path="/u/:username" element={<UsernameRedirectPage />} />
                    <Route path="/join/:inviteCode" element={<JoinGroupPage />} />
                    <Route path="/services" element={<ServicesPage />} />
                    <Route path="/plus" element={<PlusPage />} />
                    <Route path="/services/:service" element={<ServiceChannelPage />} />
                    <Route path="/help/*" element={<HelpPage />} />
                  </Route>
                </Route>

                <Route path="*" element={<NotFound />} />
              </Routes>
              </Suspense>
              <OpenInAppBanner />
            </BrowserRouter>
            </WatchTogetherProvider>
          </RealtimeProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
